import React, {useState, useRef, useEffect} from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./Header.css";
import BuyCreditsPage from "../BuyCredits/BuyCreditsPage";
import LogoUploadModal from "../Folder System/LogoUploadModal";

const Header = ({setLogoFile, setWatermarkFile, eventName, setEventId, setUserId, hideAssets = false, showFolderBar = false}) => {
    const history = useHistory();
    const location = useLocation();
    const [logo, setLogo] = useState([]);
    const [watermark, setWatermark] = useState([]);
    const logoFileInputRef = useRef();
    const watermarkFileInputRef = useRef();
    const [logoText, setLogoText] = useState("Upload event logo");
    const [watermarkText, setWatermarkText] = useState("Upload Photographer Watermark");
    const [logoUrl, setLogoUrl] = useState(localStorage.getItem("currentLogoUrl"));
    const [watermarkUrl, setWatermarkUrl] = useState(localStorage.getItem("currentWatermarkUrl"));
    const [previewModal, setPreviewModal] = useState({ show: false, url: "", title: "" });

    // Folder-bar state (search, credits, buy credits, settings)
    const [credits, setCredits] = useState(null);
    const [vendorPhone, setVendorPhone] = useState('');
    const [showBuyCreditsPage, setShowBuyCreditsPage] = useState(false);
    const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
    const [showLogoUpload, setShowLogoUpload] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState(new URLSearchParams(window.location.search).get('q') || '');
    const [showAccessControl, setShowAccessControl] = useState(false);
    const [downloadEnabled, setDownloadEnabled] = useState(true);
    const [shareEnabled, setShareEnabled] = useState(true);
    const [accessSaving, setAccessSaving] = useState(false);
    const settingsRef = useRef(null);
    const profileRef = useRef(null);

    const baseURL = process.env.REACT_APP_API_BASE_URL;

    const isPaidPackage = localStorage.getItem('currentPaidPackage') !== 'false';

    const openAccessControl = () => {
        setDownloadEnabled(localStorage.getItem('currentDownloadEnabled') !== 'false');
        setShareEnabled(localStorage.getItem('currentShareEnabled') !== 'false');
        setShowSettingsDropdown(false);
        setShowAccessControl(true);
    };

    const saveAccessControl = async () => {
        setAccessSaving(true);
        try {
            const eventId = localStorage.getItem('currentEventId');
            const vendorId = localStorage.getItem('vendorId');
            await fetch(`${baseURL}/event/vendor/permissions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': process.env.REACT_APP_BROWSER_API_KEY,
                },
                body: JSON.stringify({
                    event_id: eventId,
                    vendor_id: vendorId,
                    download_enabled: downloadEnabled,
                    share_enabled: shareEnabled,
                }),
            });
            localStorage.setItem('currentDownloadEnabled', String(downloadEnabled));
            localStorage.setItem('currentShareEnabled', String(shareEnabled));
            setShowAccessControl(false);
            history.push('/eventlist');
        } catch (e) {
        } finally {
            setAccessSaving(false);
        }
    };

    React.useEffect(() => {
        // Pre-fill from localStorage if present (coming from event click)
        try {
            const lsEventId = localStorage.getItem('currentEventId');
            const lsUserId = localStorage.getItem('uid');
            const lsLogoUrl = localStorage.getItem('currentLogoUrl');
            const lsWatermarkUrl = localStorage.getItem('currentWatermarkUrl');
            if (lsEventId) {
                setEventId(lsEventId);
            }
            if (lsUserId) {
                setUserId(lsUserId);
            }
            if (lsLogoUrl) {
                setLogoUrl(lsLogoUrl);
                setLogoText("Logo Uploaded");
            }
            if (lsWatermarkUrl) {
                setWatermarkUrl(lsWatermarkUrl);
                setWatermarkText("Watermark Uploaded");
            }
        } catch (e) { /* ignore */ }
    }, [setEventId, setUserId]);

    useEffect(() => {
        if (!showFolderBar) return;
        const vendorId = localStorage.getItem('vendorId');
        if (!vendorId) return;
        fetch(`${baseURL}/vendor?vendor_id=${encodeURIComponent(vendorId)}`, {
            headers: { 'x-api-key': process.env.REACT_APP_BROWSER_API_KEY },
        })
            .then((res) => res.ok ? res.json() : null)
            .then((data) => {
                if (data) {
                    const vendor = data.vendor || data;
                    setCredits(vendor.photo_credits ?? 0);
                    const rawMobile = Array.isArray(vendor.mobile) ? vendor.mobile[0] : (vendor.mobile || '');
                    setVendorPhone(rawMobile);
                }
            })
            .catch(() => {});
    }, [showFolderBar, baseURL]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (settingsRef.current && !settingsRef.current.contains(e.target)) {
                setShowSettingsDropdown(false);
            }
            if (profileRef.current && !profileRef.current.contains(e.target)) {
                setShowProfileDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setSearchQuery(val);
        if (location.pathname === '/folders') {
            history.replace({ pathname: '/folders', search: val ? `?q=${encodeURIComponent(val)}` : '' });
        } else {
            history.push({ pathname: '/folders', search: val ? `?q=${encodeURIComponent(val)}` : '' });
        }
    };

    const handleLogout = () => {
        localStorage.clear();
        history.push("/");
    };


    const closePreview = () => {
        setPreviewModal({ show: false, url: "", title: "" });
    };

    return (
        <>
            <div className="header">
                <div className="header-top">
                    <h1 className="header-title">{eventName}</h1>
                    <div className="header-top-right">
                        {showFolderBar && (
                            <>
                                {/* Search */}
                                <div className="hdr-search-bar">
                                    <img src="https://img.icons8.com/ios-glyphs/30/search.png" alt="search" />
                                    <input
                                        type="text"
                                        placeholder="Search folders..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                    />
                                </div>

                                {/* Credits badge */}
                                {credits !== null && (
                                    <div className={`hdr-credits-badge${credits === 0 ? ' hdr-credits-badge--empty' : ''}`}>
                                        🪙 <span>{new Intl.NumberFormat('en-IN').format(credits)}</span> Credits
                                    </div>
                                )}

                                {/* Buy Credits */}
                                <button className="hdr-buy-credits-btn" onClick={() => setShowBuyCreditsPage(true)}>
                                    + Buy Credits
                                </button>

                                {/* Settings */}
                                <div className="hdr-settings-wrapper" ref={settingsRef}>
                                    <button
                                        className="hdr-settings-btn"
                                        onClick={() => setShowSettingsDropdown((v) => !v)}
                                        aria-label="Settings"
                                    >
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3"/>
                                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                        </svg>
                                    </button>
                                    {showSettingsDropdown && (
                                        <div className="hdr-settings-dropdown">
                                            {!isPaidPackage && (
                                            <button className="hdr-settings-option" onClick={openAccessControl}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                                                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                                                </svg>
                                                Access Control
                                            </button>
                                            )}
                                            <button className="hdr-settings-option" onClick={() => { setShowSettingsDropdown(false); setShowLogoUpload(true); }}>
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                                    <polyline points="21 15 16 10 5 21"/>
                                                </svg>
                                                Logo Upload
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Profile avatar with dropdown */}
                                <div className="hdr-profile-wrapper" ref={profileRef}>
                                    <img
                                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(typeof window !== 'undefined' ? (localStorage.getItem('vendorDisplayName') || 'Photographer') : 'Photographer')}&background=F80353&color=fff&size=150`}
                                        alt="Profile"
                                        className="profile-photo-header"
                                        onClick={() => setShowProfileDropdown((v) => !v)}
                                    />
                                    {showProfileDropdown && (
                                        <div className="profile-dropdown">
                                            <button className="profile-menu-btn" onClick={() => { setShowProfileDropdown(false); history.push('/profile'); }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                                    <circle cx="12" cy="7" r="4"/>
                                                </svg>
                                                Edit Profile
                                            </button>
                                            <button className="profile-menu-btn" onClick={() => { setShowProfileDropdown(false); history.push('/transactions'); }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="5" width="20" height="14" rx="2"/>
                                                    <line x1="2" y1="10" x2="22" y2="10"/>
                                                </svg>
                                                Transactions
                                            </button>
                                            <button className="profile-menu-btn" onClick={() => { setShowProfileDropdown(false); history.push('/portfolio'); }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="2" y="3" width="20" height="14" rx="2"/>
                                                    <path d="M8 21h8M12 17v4"/>
                                                </svg>
                                                Portfolio
                                            </button>
                                            <button className="logout-btn" onClick={() => { setShowProfileDropdown(false); handleLogout(); }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                                    <polyline points="16 17 21 12 16 7"/>
                                                    <line x1="21" y1="12" x2="9" y2="12"/>
                                                </svg>
                                                Log out
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        {/* Profile + logout — hidden on /folders */}
                        {!showFolderBar && (
                            <div className="profile-container">
                                <div className="profile-icon-header">
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                        <circle cx="12" cy="7" r="4"/>
                                    </svg>
                                </div>
                                <button className="logout-overlay-btn" onClick={handleLogout} title="Logout">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                                        <polyline points="16 17 21 12 16 7"></polyline>
                                        <line x1="21" y1="12" x2="9" y2="12"></line>
                                    </svg>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
              

                {previewModal.show && (
                    <div className="preview-modal" onClick={closePreview}>
                        <div className="preview-modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="preview-modal-header">
                                <h2>{previewModal.title}</h2>
                                <button className="preview-close-btn" onClick={closePreview}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            <img src={previewModal.url} alt={previewModal.title} className="preview-image" />
                        </div>
                    </div>
                )}
            </div>
           

            {showBuyCreditsPage && (
                <BuyCreditsPage
                    onClose={() => setShowBuyCreditsPage(false)}
                    onSuccess={() => {
                        const vendorId = localStorage.getItem('vendorId');
                        if (vendorId) {
                            fetch(`${baseURL}/vendor?vendor_id=${encodeURIComponent(vendorId)}`, {
                                headers: { 'x-api-key': process.env.REACT_APP_BROWSER_API_KEY },
                            })
                                .then((res) => res.ok ? res.json() : null)
                                .then((data) => {
                                    if (data) setCredits((data.vendor || data).photo_credits ?? 0);
                                })
                                .catch(() => {});
                        }
                    }}
                    phone={vendorPhone}
                />
            )}

            {showLogoUpload && (
                <LogoUploadModal onClose={() => setShowLogoUpload(false)} />
            )}

            {showAccessControl && (
                <div className="hdr-ac-overlay" onClick={() => setShowAccessControl(false)}>
                    <div className="hdr-ac-modal" onClick={e => e.stopPropagation()}>
                        <div className="hdr-ac-header">
                            <h2>Access Control</h2>
                            <button className="hdr-ac-close" onClick={() => setShowAccessControl(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                </svg>
                            </button>
                        </div>
                        <p className="hdr-ac-sub">Control what guests can do with photos in this event.</p>

                        <div className="hdr-ac-toggle-row">
                            <div className="hdr-ac-toggle-info">
                                <span className="hdr-ac-toggle-label">Download Photos</span>
                                <span className="hdr-ac-toggle-desc">Allow guests to download photos</span>
                            </div>
                            <button
                                className={`hdr-ac-toggle${downloadEnabled ? ' hdr-ac-toggle--on' : ''}`}
                                onClick={() => setDownloadEnabled(v => !v)}
                            >
                                <span className="hdr-ac-toggle-thumb" />
                            </button>
                        </div>

                        <div className="hdr-ac-toggle-row">
                            <div className="hdr-ac-toggle-info">
                                <span className="hdr-ac-toggle-label">Share Photos</span>
                                <span className="hdr-ac-toggle-desc">Allow guests to share photos</span>
                            </div>
                            <button
                                className={`hdr-ac-toggle${shareEnabled ? ' hdr-ac-toggle--on' : ''}`}
                                onClick={() => setShareEnabled(v => !v)}
                            >
                                <span className="hdr-ac-toggle-thumb" />
                            </button>
                        </div>

                        <div className="hdr-ac-footer">
                            <button className="hdr-ac-cancel" onClick={() => setShowAccessControl(false)}>Cancel</button>
                            <button className="hdr-ac-save" onClick={saveAccessControl} disabled={accessSaving}>
                                {accessSaving ? 'Saving…' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header;
